import React, { useState, useEffect } from 'react';
import { 
  Discount, 
  CreateDiscountDto, 
  DiscountType, 
  ApplicableVehicleType 
} from '../../types/discount';
import { discountService } from '../../services/discountService';

interface DiscountFormProps {
  discount?: Discount | null;
  onSubmit: (discountData: CreateDiscountDto) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const DiscountForm: React.FC<DiscountFormProps> = ({
  discount,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<CreateDiscountDto>({
    code: '',
    name: '',
    description: '',
    type: DiscountType.PERCENTAGE,
    value: 0,
    validFrom: '',
    validUntil: '',
    usageLimit: 0,
    usagePerCustomer: 1,
    minimumRentalDays: 0,
    maximumRentalDays: 0,
    minimumAmount: 0,
    maximumDiscountAmount: 0,
    applicableVehicleType: ApplicableVehicleType.ALL,
    specificVehicles: [],
    applicableLocations: [],
    excludedLocations: [],
    isFirstTimeCustomerOnly: false,
    isRecurringCustomerOnly: false,
    applicableDaysOfWeek: [],
    blackoutDates: [],
    canCombineWithOtherDiscounts: false,
    isAutoApply: false,
    priority: 0,
    internalNotes: '',
    isPublic: true,
    marketingCampaign: '',
    budgetLimit: 0
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showAdvanced, setShowAdvanced] = useState(false);

  const daysOfWeek = [
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
  ];

  const vehicleTypes = [
    { value: ApplicableVehicleType.ALL, label: 'All Vehicle Types' },
    { value: ApplicableVehicleType.ECONOMY, label: 'Economy' },
    { value: ApplicableVehicleType.COMPACT, label: 'Compact' },
    { value: ApplicableVehicleType.MIDSIZE, label: 'Midsize' },
    { value: ApplicableVehicleType.FULLSIZE, label: 'Full Size' },
    { value: ApplicableVehicleType.LUXURY, label: 'Luxury' },
    { value: ApplicableVehicleType.SUV, label: 'SUV' },
    { value: ApplicableVehicleType.SPECIFIC_VEHICLES, label: 'Specific Vehicles' }
  ];

  useEffect(() => {
    if (discount) {
      setFormData({
        code: discount.code,
        name: discount.name,
        description: discount.description || '',
        type: discount.type,
        value: discount.value,
        validFrom: formatDateForInput(discount.validFrom),
        validUntil: formatDateForInput(discount.validUntil),
        usageLimit: discount.usageLimit,
        usagePerCustomer: discount.usagePerCustomer,
        minimumRentalDays: discount.minimumRentalDays,
        maximumRentalDays: discount.maximumRentalDays,
        minimumAmount: discount.minimumAmount,
        maximumDiscountAmount: discount.maximumDiscountAmount,
        applicableVehicleType: discount.applicableVehicleType,
        specificVehicles: discount.specificVehicles,
        applicableLocations: discount.applicableLocations,
        excludedLocations: discount.excludedLocations,
        isFirstTimeCustomerOnly: discount.isFirstTimeCustomerOnly,
        isRecurringCustomerOnly: discount.isRecurringCustomerOnly,
        applicableDaysOfWeek: discount.applicableDaysOfWeek,
        blackoutDates: discount.blackoutDates?.map(date => formatDateForInput(new Date(date))) || [],
        canCombineWithOtherDiscounts: discount.canCombineWithOtherDiscounts,
        isAutoApply: discount.isAutoApply,
        priority: discount.priority,
        internalNotes: discount.internalNotes || '',
        isPublic: discount.isPublic,
        marketingCampaign: discount.marketingCampaign || '',
        budgetLimit: discount.budgetLimit
      });
      setShowAdvanced(true); // Show advanced options when editing
    }
  }, [discount]);

  const formatDateForInput = (date: Date | string): string => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : 
              type === 'checkbox' ? (e.target as HTMLInputElement).checked :
              value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleArrayInputChange = (name: string, value: string) => {
    const values = value.split(',').map(v => v.trim()).filter(v => v);
    setFormData(prev => ({ ...prev, [name]: values }));
  };

  const handleDayOfWeekChange = (day: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      applicableDaysOfWeek: checked 
        ? [...prev.applicableDaysOfWeek!, day]
        : prev.applicableDaysOfWeek!.filter(d => d !== day)
    }));
  };

  const generateCode = async () => {
    const code = await discountService.generateDiscountCode();
    setFormData(prev => ({ ...prev, code }));
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.code.trim()) newErrors.code = 'Discount code is required';
    if (!formData.name.trim()) newErrors.name = 'Discount name is required';
    if (!formData.type) newErrors.type = 'Discount type is required';
    if (formData.value <= 0) newErrors.value = 'Discount value must be greater than 0';
    
    if (formData.type === DiscountType.PERCENTAGE && formData.value > 100) {
      newErrors.value = 'Percentage discount cannot exceed 100%';
    }

    if (!formData.validFrom) newErrors.validFrom = 'Valid from date is required';
    if (!formData.validUntil) newErrors.validUntil = 'Valid until date is required';
    
    if (formData.validFrom && formData.validUntil) {
      const fromDate = new Date(formData.validFrom);
      const untilDate = new Date(formData.validUntil);
      if (fromDate >= untilDate) {
        newErrors.validUntil = 'Valid until date must be after valid from date';
      }
    }

    if (formData.usagePerCustomer && formData.usagePerCustomer < 1) {
      newErrors.usagePerCustomer = 'Usage per customer must be at least 1';
    }

    if (formData.minimumRentalDays && formData.maximumRentalDays && 
        formData.minimumRentalDays > formData.maximumRentalDays) {
      newErrors.maximumRentalDays = 'Maximum rental days must be greater than minimum';
    }

    if (formData.isFirstTimeCustomerOnly && formData.isRecurringCustomerOnly) {
      newErrors.isRecurringCustomerOnly = 'Cannot be both first-time and recurring customer only';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">
          {discount ? 'Edit Discount' : 'Create New Discount'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount Code *
              </label>
              <div className="flex">
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  className={`flex-1 px-3 py-2 border rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.code ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., SUMMER2024"
                />
                <button
                  type="button"
                  onClick={generateCode}
                  className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200 text-sm"
                >
                  Generate
                </button>
              </div>
              {errors.code && <p className="text-red-500 text-sm mt-1">{errors.code}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Summer Sale 2024"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount Type *
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.type ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value={DiscountType.PERCENTAGE}>Percentage</option>
                <option value={DiscountType.FIXED_AMOUNT}>Fixed Amount</option>
                <option value={DiscountType.FREE_DAYS}>Free Days</option>
              </select>
              {errors.type && <p className="text-red-500 text-sm mt-1">{errors.type}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount Value *
              </label>
              <div className="relative">
                {formData.type === DiscountType.FIXED_AMOUNT && (
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                )}
                <input
                  type="number"
                  name="value"
                  value={formData.value}
                  onChange={handleInputChange}
                  min="0"
                  step={formData.type === DiscountType.FIXED_AMOUNT ? "0.01" : "1"}
                  max={formData.type === DiscountType.PERCENTAGE ? "100" : undefined}
                  className={`w-full ${formData.type === DiscountType.FIXED_AMOUNT ? 'pl-8' : 'pl-3'} pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.value ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {formData.type === DiscountType.PERCENTAGE && (
                  <span className="absolute right-3 top-2 text-gray-500">%</span>
                )}
                {formData.type === DiscountType.FREE_DAYS && (
                  <span className="absolute right-3 top-2 text-gray-500">days</span>
                )}
              </div>
              {errors.value && <p className="text-red-500 text-sm mt-1">{errors.value}</p>}
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Discount description..."
            />
          </div>
        </div>

        {/* Validity Period */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Validity Period</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valid From *
              </label>
              <input
                type="date"
                name="validFrom"
                value={formData.validFrom}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.validFrom ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.validFrom && <p className="text-red-500 text-sm mt-1">{errors.validFrom}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valid Until *
              </label>
              <input
                type="date"
                name="validUntil"
                value={formData.validUntil}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.validUntil ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.validUntil && <p className="text-red-500 text-sm mt-1">{errors.validUntil}</p>}
            </div>
          </div>
        </div>

        {/* Usage Limits */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Usage Limits</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Usage Limit
              </label>
              <input
                type="number"
                name="usageLimit"
                value={formData.usageLimit}
                onChange={handleInputChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0 = unlimited"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Usage Per Customer
              </label>
              <input
                type="number"
                name="usagePerCustomer"
                value={formData.usagePerCustomer}
                onChange={handleInputChange}
                min="1"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.usagePerCustomer ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.usagePerCustomer && <p className="text-red-500 text-sm mt-1">{errors.usagePerCustomer}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Budget Limit
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  type="number"
                  name="budgetLimit"
                  value={formData.budgetLimit}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0 = unlimited"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Vehicle Type */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Applicable Vehicles</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vehicle Type
            </label>
            <select
              name="applicableVehicleType"
              value={formData.applicableVehicleType}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {vehicleTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Advanced Options Toggle */}
        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <span className="mr-2">
              {showAdvanced ? '▼' : '▶'}
            </span>
            Advanced Options
          </button>
        </div>

        {/* Advanced Options */}
        {showAdvanced && (
          <>
            {/* Rental Requirements */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Rental Requirements</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Rental Days
                  </label>
                  <input
                    type="number"
                    name="minimumRentalDays"
                    value={formData.minimumRentalDays}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Rental Days
                  </label>
                  <input
                    type="number"
                    name="maximumRentalDays"
                    value={formData.maximumRentalDays}
                    onChange={handleInputChange}
                    min="0"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.maximumRentalDays ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0 = no limit"
                  />
                  {errors.maximumRentalDays && <p className="text-red-500 text-sm mt-1">{errors.maximumRentalDays}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Booking Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      name="minimumAmount"
                      value={formData.minimumAmount}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {formData.type === DiscountType.PERCENTAGE && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Discount Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      name="maximumDiscountAmount"
                      value={formData.maximumDiscountAmount}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0 = no limit"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Customer Eligibility */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Eligibility</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isFirstTimeCustomerOnly"
                    checked={formData.isFirstTimeCustomerOnly}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    First-time customers only
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isRecurringCustomerOnly"
                    checked={formData.isRecurringCustomerOnly}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Returning customers only
                  </label>
                </div>
                {errors.isRecurringCustomerOnly && (
                  <p className="text-red-500 text-sm">{errors.isRecurringCustomerOnly}</p>
                )}
              </div>
            </div>

            {/* Days of Week */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Applicable Days</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {daysOfWeek.map(day => (
                  <div key={day} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.applicableDaysOfWeek?.includes(day) || false}
                      onChange={(e) => handleDayOfWeekChange(day, e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900 capitalize">
                      {day}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Leave all unchecked to apply to all days
              </p>
            </div>

            {/* Locations */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Location Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Applicable Locations
                  </label>
                  <input
                    type="text"
                    value={formData.applicableLocations?.join(', ') || ''}
                    onChange={(e) => handleArrayInputChange('applicableLocations', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Downtown, Airport (comma separated)"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Leave empty to apply to all locations
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Excluded Locations
                  </label>
                  <input
                    type="text"
                    value={formData.excludedLocations?.join(', ') || ''}
                    onChange={(e) => handleArrayInputChange('excludedLocations', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Maintenance Center (comma separated)"
                  />
                </div>
              </div>
            </div>

            {/* Additional Settings */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Settings</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <input
                      type="number"
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Higher number = higher priority"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Marketing Campaign
                    </label>
                    <input
                      type="text"
                      name="marketingCampaign"
                      value={formData.marketingCampaign}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Summer 2024 Campaign"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="isAutoApply"
                      checked={formData.isAutoApply}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Auto-apply when conditions are met
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="canCombineWithOtherDiscounts"
                      checked={formData.canCombineWithOtherDiscounts}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Can combine with other discounts
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="isPublic"
                      checked={formData.isPublic}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Publicly visible
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Internal Notes
                  </label>
                  <textarea
                    name="internalNotes"
                    value={formData.internalNotes}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Internal notes for admin use..."
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : discount ? 'Update Discount' : 'Create Discount'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DiscountForm;